import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import ProxyLayer from 'base/api/ProxyLayer';
import {
  FieldConditionsRequest,
  FieldConditionsResponse,
  SoilPropertiesRequest,
} from 'base/types/FieldConditions';

export default function useFieldConditions(params: FieldConditionsRequest | null) {
  const { organizationId, lat, lon, plantingDate, harvestDate } = params ?? {};
  const soilKey = JSON.stringify(params?.soil ?? null);
  const key = JSON.stringify([organizationId, lat, lon, plantingDate, harvestDate, soilKey]);
  const cache = useRef(new Map<string, FieldConditionsResponse>());
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<{
    key: string;
    loading: boolean;
    data: FieldConditionsResponse | null;
    error: boolean;
  }>({ key: '', loading: false, data: null, error: false });

  useEffect(() => {
    if (!organizationId || lat === undefined || lon === undefined) {
      return;
    }
    const cached = cache.current.get(key);
    if (cached) {
      setState({ key, loading: false, data: cached, error: false });
      return;
    }
    const source = ProxyLayer.cancelToken();
    let active = true;
    const soil: SoilPropertiesRequest | undefined =
      soilKey === 'null' ? undefined : JSON.parse(soilKey);
    setState({ key, loading: true, data: null, error: false });
    ProxyLayer.getFieldConditions(
      {
        organizationId,
        lat,
        lon,
        plantingDate,
        harvestDate,
        ...(soil ? { soil } : {}),
      },
      source.token
    )
      .then((data) => {
        if (!active) {
          return;
        }
        cache.current.set(key, data);
        setState({ key, loading: false, data, error: false });
      })
      .catch((error: unknown) => {
        if (active && !axios.isCancel(error)) {
          setState({ key, loading: false, data: null, error: true });
        }
      });
    return () => {
      active = false;
      source.cancel();
    };
  }, [key, organizationId, lat, lon, plantingDate, harvestDate, soilKey, attempt]);

  const retry = () => {
    cache.current.delete(key);
    setAttempt((current) => current + 1);
  };
  return {
    data: state.key === key ? state.data : null,
    loading: !!params && (state.key !== key || state.loading),
    error: state.key === key && state.error,
    retry,
  };
}
